// frontend-react/src/services/csvParser.ts
import { RuleDraft } from '../types/rule';

/**
 * 解析單行 CSV（處理引號內的逗號和換行）
 */
export const parseCSVRow = (line: string): string[] => {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current);

  return result;
};

/**
 * 解析 CSV 內容為規則列表（新格式：RuleDraft）
 * - 支援欄位名稱中英文混用：
 *   id/序號/項次
 *   name/規則名稱/審查規則名稱
 *   original_text/規則內容/審查規則內容/content
 */
export const parseCSV = (text: string): RuleDraft[] => {
  const lines = text.split('\n').filter(line => line.trim());
  if (lines.length < 2) {
    throw new Error('CSV 檔案至少需要包含標題行和一筆資料');
  }

  // 解析標題行
  const headerLine = lines[0];
  if (!headerLine) {
    throw new Error('CSV 檔案格式錯誤：無法讀取標題行');
  }
  const headers = parseCSVRow(headerLine).map(h => h.trim());

  const idCol = headers.findIndex(h => ['項次', '序號', 'id', 'ID'].includes(h));
  const nameCol = headers.findIndex(h => ['規則名稱', '審查規則名稱', 'name', 'Name'].includes(h));
  const contentCol = headers.findIndex(h =>
    ['規則內容', '審查規則內容', 'content', 'Content', 'original_text', 'OriginalText'].includes(h)
  );

  if (idCol === -1 || nameCol === -1 || contentCol === -1) {
    throw new Error(
      'CSV 格式錯誤：缺少必要欄位。需要包含：項次/序號(id)、規則名稱(name)、規則內容(original_text)'
    );
  }

  const rules: RuleDraft[] = [];

  // 處理可能跨行的內容（引號內換行）
  let currentLine = '';
  for (let i = 1; i < lines.length; i++) {
    currentLine += (currentLine ? '\n' : '') + lines[i];

    const quoteCount = (currentLine.match(/"/g) || []).length;
    if (quoteCount % 2 !== 0) {
      continue; // 還在引號內，繼續讀取下一行
    }

    const row = parseCSVRow(currentLine);

    if (row.length >= Math.max(idCol, nameCol, contentCol) + 1) {
      const id = (row[idCol] ?? '').trim();
      const name = (row[nameCol] ?? '').trim();
      const originalText = (row[contentCol] ?? '').trim();

      const ruleDraft: RuleDraft = {
        key: `rule-${rules.length}`,
        id: id || String(rules.length + 1),
        name,
        original_text: originalText,
        ai_text: '',
        current_version: 'original',
      };

      // 基本有效性檢查：至少要有 id/name/original_text
      if (ruleDraft.id && ruleDraft.name && ruleDraft.original_text) {
        rules.push(ruleDraft);
      }
    }

    currentLine = '';
  }

  if (rules.length === 0) {
    throw new Error('CSV 檔案無有效規則資料');
  }

  return rules;
};

/**
 * 讀取檔案內容為文字
 */
export const readFileAsText = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      const result = e.target?.result;
      if (typeof result === 'string') {
        resolve(result);
      } else {
        reject(new Error('無法讀取檔案內容'));
      }
    };
    reader.onerror = () => reject(new Error('檔案讀取失敗'));
    reader.readAsText(file, 'UTF-8');
  });
};
